import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { toast } from "sonner";

/**
 * Reusable toggle to enable/disable push notifications.
 *
 * props:
 *  - variant: "button" | "card"  (default: "button")
 *  - compact: boolean
 */
export function NotificationsButton({ variant = "button", compact = false }) {
  const { supported, canSubscribe, isIOS, standalone, subscribed, busy, subscribe, unsubscribe, permission } =
    usePushNotifications();

  if (!supported) {
    return variant === "card" ? (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="push-unsupported">
        Il tuo browser non supporta le notifiche push.
      </div>
    ) : null;
  }

  // iOS + non-installato → mostra istruzioni Aggiungi a Home
  if (isIOS && !standalone) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="push-ios-add-home">
        <div className="font-semibold">Attiva le notifiche su iPhone/iPad</div>
        <p className="mt-1">
          Tocca il pulsante <strong>Condividi</strong> in Safari e poi <strong>Aggiungi alla schermata Home</strong>. Riapri l'app installata e torna qui per abilitare le notifiche.
        </p>
      </div>
    );
  }

  const handle = async () => {
    if (subscribed) {
      await unsubscribe();
      toast.success("Notifiche disattivate");
      return;
    }
    const res = await subscribe();
    if (res.ok) toast.success("Notifiche attivate su questo dispositivo");
    else if (res.reason === "denied") toast.error("Permesso notifiche negato. Abilitalo dalle impostazioni del browser.");
    else toast.error("Impossibile attivare le notifiche");
  };

  if (variant === "card") {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between gap-3" data-testid="push-card">
        <div>
          <div className="font-semibold flex items-center gap-2">
            {subscribed ? <Bell className="w-4 h-4 text-emerald-600" /> : <BellOff className="w-4 h-4 text-slate-500" />}
            Notifiche push
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {subscribed
              ? "Ricevi avvisi su lavori, preventivi, messaggi e offerte."
              : "Attiva per non perdere nuovi lavori, preventivi e messaggi."}
          </div>
          {permission === "denied" && (
            <div className="text-xs text-rose-600 mt-1">Permesso bloccato dal browser. Riabilitalo nelle impostazioni del sito.</div>
          )}
        </div>
        <Button onClick={handle} disabled={busy || !canSubscribe} variant={subscribed ? "outline" : "default"} data-testid="push-toggle-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : subscribed ? "Disattiva" : "Attiva"}
        </Button>
      </div>
    );
  }

  return (
    <Button
      onClick={handle}
      disabled={busy || !canSubscribe}
      size={compact ? "sm" : "default"}
      variant={subscribed ? "outline" : "default"}
      data-testid="push-toggle-btn"
      className="gap-2"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : subscribed ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
      {subscribed ? "Notifiche attive" : "Attiva notifiche"}
    </Button>
  );
}

export default NotificationsButton;
